import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, Loader2, Plus, X as XIcon, FileText, AlertCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import AutoTextarea from "./AutoTextarea";

/**
 * ProtocolSuggestPanel
 * - Mostra gli hashtag clinici della visita (editable, removable, manuali o da IA)
 * - Per ciascun hashtag con template attivo del medico → pulsante "Inserisci protocollo #x"
 * - Per hashtag senza template → pulsante "Genera protocollo" (chiama IA con sorgenti verificabili)
 * - Pulsante "Inserisci tutti" se almeno 2 template disponibili
 * - Append/merge senza duplicati (controllo per "Indicazioni specifiche (...)" key)
 * - Textarea modificabile dal medico
 *
 * Props:
 *  - hashtags: string[]              (#pterigio, #glaucoma, ...)
 *  - setHashtags: fn(string[])
 *  - value: string                    (testo protocollo corrente)
 *  - onChange: fn(string)
 *  - readOnly: bool
 *  - diagnosi: string                 (per generare template ad-hoc)
 *  - context: {anamnesi, OD, OS, diagnosi, terapia}  (legacy AI fallback)
 *  - testid: prefix testid
 */

function normHashtag(raw) {
  let t = String(raw || "").trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
  if (!t) return "";
  if (!t.startsWith("#")) t = "#" + t;
  return t.replace(/[^#a-z0-9_]/g, "").slice(0, 40);
}

function uniqueAppend(existing, addition) {
  if (!existing || !existing.trim()) return addition;
  // Evita duplicato se "Indicazioni specifiche (...)" è già presente
  const m = addition.match(/Indicazioni specifiche \(([^)]+)\):/i);
  if (m) {
    const key = m[1].trim().toLowerCase();
    if (existing.toLowerCase().includes(`indicazioni specifiche (${key})`)) {
      return existing;
    }
  }
  return `${existing.trim()}\n\n${addition.trim()}`;
}

export default function ProtocolSuggestPanel({
  hashtags = [],
  setHashtags,
  value,
  onChange,
  readOnly,
  diagnosi,
  context,
  testid = "protocol-panel",
}) {
  const [matchesByTag, setMatchesByTag] = useState({}); // {hashtag: {found, template}}
  const [baseText, setBaseText] = useState("");
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [generating, setGenerating] = useState(null); // hashtag in generazione
  const [hashtagInput, setHashtagInput] = useState("");

  const hashtagsKey = useMemo(() => hashtags.join(","), [hashtags]);

  // Carica i match dal backend ogni volta che cambia il set di hashtag
  useEffect(() => {
    let cancelled = false;
    const fetchMatches = async () => {
      if (!hashtags || hashtags.length === 0) {
        setMatchesByTag({});
        return;
      }
      setLoadingMatch(true);
      try {
        const { data } = await api.post("/medico/protocol-templates/match", { hashtags });
        if (cancelled) return;
        const map = {};
        (data.matches || []).forEach((m) => { map[m.hashtag] = m; });
        setMatchesByTag(map);
        setBaseText(data.base_text || "");
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e, "Errore caricamento protocolli"));
      } finally {
        if (!cancelled) setLoadingMatch(false);
      }
    };
    fetchMatches();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashtagsKey]);

  const insertProtocol = (tag) => {
    const m = matchesByTag[tag];
    if (!m || !m.found || !m.template) return;
    const addition = m.template.protocol_text || "";
    onChange?.(uniqueAppend(value || "", addition));
    toast.success(`Protocollo ${tag} inserito`);
  };

  const insertAll = () => {
    const available = hashtags.filter((t) => matchesByTag[t]?.found);
    if (available.length === 0) return;
    let next = value || "";
    let inserted = 0;
    available.forEach((tag) => {
      const tpl = matchesByTag[tag]?.template;
      if (!tpl) return;
      const before = next;
      next = uniqueAppend(next, tpl.protocol_text || "");
      if (next !== before) inserted += 1;
    });
    onChange?.(next);
    if (inserted === 0) {
      toast.info("Nessun nuovo protocollo da inserire (già presenti)");
    } else if (inserted === 1) {
      toast.success("1 protocollo inserito");
    } else {
      toast.success(`${inserted} protocolli inseriti`);
    }
  };

  const generateForTag = async (tag) => {
    if (!tag) return;
    setGenerating(tag);
    try {
      // Patologia: usa diagnosi corrente se contiene match testuale, altrimenti deriva da hashtag
      const fallbackPath = tag.replace(/^#/, "").replace(/_/g, " ");
      const { data } = await api.post("/medico/protocol-templates/generate", {
        pathology: (diagnosi && diagnosi.trim()) ? diagnosi.trim().split("\n")[0].slice(0, 120) : fallbackPath,
        hashtag: tag,
        extra_notes: (context?.anamnesi || "").slice(0, 300),
      });
      // Append draft testo direttamente nella textarea, lasciando il medico modificare/salvare poi nei Settings
      onChange?.(uniqueAppend(value || "", data.protocol_text || ""));
      toast.success(`Bozza protocollo ${tag} generata — verifica e salva nei tuoi modelli`);
      if (data.warning) toast.info(data.warning, { duration: 8000 });
      if (data.sources && data.sources.length) {
        toast.info(`Fonti: ${data.sources.join(" · ")}`, { duration: 10000 });
      }
    } catch (e) {
      toast.error(apiErr(e, "Errore generazione protocollo IA"));
    } finally {
      setGenerating(null);
    }
  };

  const addHashtagManual = () => {
    const t = normHashtag(hashtagInput);
    if (!t || t === "#") return;
    if (hashtags.includes(t)) {
      toast.info("Hashtag già presente");
      setHashtagInput("");
      return;
    }
    setHashtags?.([...hashtags, t]);
    setHashtagInput("");
  };

  const removeHashtag = (tag) => {
    setHashtags?.(hashtags.filter((t) => t !== tag));
  };

  const availableMatches = hashtags.filter((t) => matchesByTag[t]?.found);
  const missingMatches = hashtags.filter((t) => matchesByTag[t] && !matchesByTag[t].found);

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-5" data-testid={`${testid}-block`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <label className="text-xs font-bold uppercase tracking-wider text-brand-orange flex items-center gap-1.5">
          <Sparkles size={12} /> Protocollo suggerito
        </label>
        {loadingMatch && <Loader2 className="animate-spin text-slate-400" size={14} />}
      </div>

      {/* Hashtag chips */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Hashtag clinici</div>
        <div className="flex flex-wrap gap-2" data-testid={`${testid}-hashtags`}>
          {hashtags.length === 0 && (
            <span className="text-xs text-slate-400 italic">Nessun hashtag — aggiungi manualmente o usa REC IA</span>
          )}
          {hashtags.map((t) => {
            const m = matchesByTag[t];
            const hasTemplate = m?.found;
            return (
              <span
                key={t}
                className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                  hasTemplate ? "bg-brand-azure text-white" : "bg-slate-200 text-slate-700"
                }`}
                data-testid={`${testid}-hashtag-${t.replace(/[^a-z0-9]/gi, "")}`}
              >
                {t}{!hasTemplate && <AlertCircle size={11} title="Nessun template attivo per questo hashtag" />}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeHashtag(t)}
                    data-testid={`${testid}-remove-${t.replace(/[^a-z0-9]/gi, "")}`}
                    className="hover:text-brand-orange"
                  >
                    <XIcon size={11} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
        {!readOnly && (
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={hashtagInput}
              onChange={(e) => setHashtagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHashtagManual(); } }}
              placeholder="#nuovo_hashtag"
              data-testid={`${testid}-hashtag-input`}
              className="flex-1 max-w-xs px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-xs"
            />
            <button
              type="button"
              onClick={addHashtagManual}
              data-testid={`${testid}-hashtag-add`}
              className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1"
            >
              <Plus size={11} /> Aggiungi
            </button>
          </div>
        )}
      </div>

      {/* Bottoni inserisci protocollo */}
      {!readOnly && hashtags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2" data-testid={`${testid}-actions`}>
          {availableMatches.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => insertProtocol(tag)}
              data-testid={`${testid}-insert-${tag.replace(/[^a-z0-9]/gi, "")}`}
              className="bg-brand-azure/10 hover:bg-brand-azure hover:text-white text-brand-azure border border-brand-azure font-bold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition"
            >
              <FileText size={12} /> Inserisci protocollo {tag}
            </button>
          ))}
          {availableMatches.length >= 2 && (
            <button
              type="button"
              onClick={insertAll}
              data-testid={`${testid}-insert-all`}
              className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5"
            >
              <FileText size={12} /> Inserisci tutti ({availableMatches.length})
            </button>
          )}
          {missingMatches.map((tag) => (
            <button
              key={`miss-${tag}`}
              type="button"
              onClick={() => generateForTag(tag)}
              disabled={generating === tag}
              data-testid={`${testid}-generate-${tag.replace(/[^a-z0-9]/gi, "")}`}
              className="bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-300 font-bold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-60"
            >
              {generating === tag ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Genera protocollo {tag}
            </button>
          ))}
        </div>
      )}

      {/* Testarea protocollo */}
      <AutoTextarea
        value={value || ""}
        onChange={(v) => onChange?.(v)}
        disabled={readOnly}
        placeholder={`Testo protocollo che verrà inserito nel referto. Es:\n\n${baseText || "Spiegato al paziente il quadro clinico…"}`}
        data-testid={`${testid}-textarea`}
        minRows={5}
        className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm leading-relaxed disabled:bg-slate-50"
      />
      <div className="mt-1 text-[10px] text-slate-400 italic">
        I protocolli sono modificabili dalle <b>Impostazioni → Protocolli per diagnosi</b>. Rimuovere un hashtag fa scomparire il relativo pulsante.
      </div>
    </div>
  );
}
